import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Shield, Zap, Flame, Droplets, Home, Wrench, Snowflake, TreePine,
  Edit, Plus, Check, X, Search, Calendar, Clock, AlertTriangle,
  ChevronRight, FileText, Building2, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const CATEGORY_CONFIG = {
  'Compliance': { icon: Shield, color: 'text-red-500', bg: 'bg-red-50' },
  'Structural': { icon: Building2, color: 'text-slate-600', bg: 'bg-slate-50' },
  'Services': { icon: Wrench, color: 'text-blue-500', bg: 'bg-blue-50' },
  'Grounds': { icon: TreePine, color: 'text-green-600', bg: 'bg-green-50' },
  'Seasonal': { icon: Snowflake, color: 'text-cyan-500', bg: 'bg-cyan-50' },
  'Electrical': { icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  'Gas': { icon: Flame, color: 'text-orange-500', bg: 'bg-orange-50' },
  'Water': { icon: Droplets, color: 'text-blue-500', bg: 'bg-blue-50' },
  'Drainage': { icon: Droplets, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  'Fire & Safety': { icon: Shield, color: 'text-red-600', bg: 'bg-red-50' },
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

const CATEGORIES = ['All', 'Compliance', 'Structural', 'Services', 'Grounds', 'Seasonal', 'Electrical', 'Gas', 'Water', 'Drainage', 'Fire & Safety'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const TRADES = ['Electrician', 'Gas Engineer', 'Plumber', 'Roofer', 'General Builder', 'Fire Safety', 'HVAC Engineer', 'Drainage', 'Multi-Trade'];

export default function PPMLibraryPage() {
  const [masters, setMasters] = useState([]);
  const [assets, setAssets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [assigningMaster, setAssigningMaster] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [mastersRes, assetsRes, dashRes] = await Promise.all([
        axios.get(`${API}/api/ppm-core/masters?active_only=false`, { headers }),
        axios.get(`${API}/api/ppm-core/assets?limit=500`, { headers }),
        axios.get(`${API}/api/ppm-core/dashboard`, { headers }),
      ]);
      setMasters(mastersRes.data.masters || []);
      setAssets(assetsRes.data.assets || []);
      setDashboard(dashRes.data.dashboard || {});
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load PPM library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredMasters = masters.filter(m => {
    const matchesCategory = selectedCategory === 'All' || m.category === selectedCategory;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (m.task_name || '').toLowerCase().includes(searchLower) ||
      (m.description || '').toLowerCase().includes(searchLower);
    return matchesCategory && matchesSearch;
  });

  const handleEditMaster = (master) => {
    setEditingMaster({ ...master });
    setShowEditDialog(true);
  };

  const handleSaveMaster = async () => {
    try {
      if (editingMaster.id) {
        await axios.put(`${API}/api/ppm-core/masters/${editingMaster.id}`, editingMaster, { headers });
        toast.success('PPM Master updated');
      } else {
        await axios.post(`${API}/api/ppm-core/masters`, editingMaster, { headers });
        toast.success('PPM Master created');
      }
      setShowEditDialog(false);
      setEditingMaster(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleToggleActive = async (master) => {
    try {
      await axios.put(`${API}/api/ppm-core/masters/${master.id}`, { active: !master.active }, { headers });
      toast.success(master.active ? 'PPM Master deactivated' : 'PPM Master activated');
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleOpenAssign = (master) => {
    setAssigningMaster(master);
    setSelectedAssets([]);
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (selectedAssets.length === 0) {
      toast.error('Select at least one asset');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/ppm-core/assign`, {
        asset_ids: selectedAssets,
        ppm_master_id: assigningMaster.id,
      }, { headers });
      toast.success(`Assigned to ${res.data.created} assets (${res.data.skipped} skipped)`);
      setShowAssignDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const getCategoryIcon = (category) => {
    const config = CATEGORY_CONFIG[category] || { icon: FileText, color: 'text-gray-500' };
    const IconComponent = config.icon;
    return <IconComponent className={`h-4 w-4 ${config.color}`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F7A78]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">PPM Master Library</h1>
          <p className="text-slate-500">Editable template library for Planned Preventive Maintenance tasks</p>
        </div>
        <Button onClick={() => { setEditingMaster({}); setShowEditDialog(true); }} className="bg-[#1F7A78] hover:bg-[#16615f]">
          <Plus className="h-4 w-4 mr-2" />
          Add New PPM Task
        </Button>
      </div>

      {/* Dashboard KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.library?.total_masters || 0}</div>
              <div className="text-xs text-slate-500">PPM Templates</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.library?.total_assets || 0}</div>
              <div className="text-xs text-slate-500">Assets</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.library?.total_assigned || 0}</div>
              <div className="text-xs text-slate-500">Active Assignments</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.schedule?.overdue || 0}</div>
              <div className="text-xs text-slate-500">Overdue</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.schedule?.due_today || 0}</div>
              <div className="text-xs text-slate-500">Due Today</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-cyan-500">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{dashboard.schedule?.due_this_week || 0}</div>
              <div className="text-xs text-slate-500">Due This Week</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Search PPM tasks..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="ppm-search-input"
            />
          </div>
        </div>
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-auto">
          <TabsList className="flex-wrap h-auto gap-1">
            {CATEGORIES.map(cat => (
              <TabsTrigger 
                key={cat} 
                value={cat} 
                className="text-xs px-3 py-1.5"
                data-testid={`category-tab-${cat.toLowerCase()}`}
              >
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* PPM Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMasters.map(master => {
          const catConfig = CATEGORY_CONFIG[master.category] || {};
          return (
            <Card 
              key={master.id} 
              className={`relative overflow-hidden ${!master.active ? 'opacity-60' : ''}`}
              data-testid={`ppm-card-${master.id}`}
            >
              <div className={`absolute top-0 left-0 w-1 h-full ${catConfig.bg?.replace('bg-', 'bg-') || 'bg-gray-200'}`} 
                   style={{ backgroundColor: catConfig.color?.includes('red') ? '#ef4444' : 
                            catConfig.color?.includes('blue') ? '#3b82f6' :
                            catConfig.color?.includes('orange') ? '#f97316' :
                            catConfig.color?.includes('green') ? '#22c55e' :
                            catConfig.color?.includes('cyan') ? '#06b6d4' :
                            catConfig.color?.includes('yellow') ? '#eab308' : '#6b7280' }} />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(master.category)}
                    <span className="text-xs text-slate-500">{master.category}</span>
                  </div>
                  <div className="flex gap-1">
                    {master.is_legal_requirement && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Legal</Badge>
                    )}
                    {master.requires_certificate && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">Cert</Badge>
                    )}
                    <Badge className={`text-xs ${PRIORITY_COLORS[master.default_priority] || 'bg-gray-100'}`}>
                      {master.default_priority}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-base mt-2">{master.task_name}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-4">
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{master.description}</p>
                
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Every {master.default_frequency_months} months
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    £{master.cost_min} - £{master.cost_max}
                  </div>
                  {master.default_trade && (
                    <div className="flex items-center gap-1 col-span-2">
                      <Wrench className="h-3 w-3" />
                      {master.default_trade}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={master.active} 
                      onCheckedChange={() => handleToggleActive(master)}
                      data-testid={`toggle-active-${master.id}`}
                    />
                    <span className="text-xs text-slate-500">{master.active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEditMaster(master)}
                      data-testid={`edit-btn-${master.id}`}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-[#1F7A78] hover:bg-[#16615f]"
                      onClick={() => handleOpenAssign(master)}
                      disabled={!master.active}
                      data-testid={`assign-btn-${master.id}`}
                    >
                      <ChevronRight className="h-3 w-3 mr-1" />
                      Assign
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredMasters.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No PPM tasks found matching your criteria
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMaster?.id ? 'Edit PPM Task' : 'Create PPM Task'}</DialogTitle>
          </DialogHeader>
          {editingMaster && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Task Name *</Label>
                  <Input 
                    value={editingMaster.task_name || ''} 
                    onChange={(e) => setEditingMaster({...editingMaster, task_name: e.target.value})}
                    data-testid="edit-task-name"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select 
                    value={editingMaster.category || ''} 
                    onValueChange={(v) => setEditingMaster({...editingMaster, category: v})}
                  >
                    <SelectTrigger data-testid="edit-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter(c => c !== 'All').map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default Priority</Label>
                  <Select 
                    value={editingMaster.default_priority || 'Medium'} 
                    onValueChange={(v) => setEditingMaster({...editingMaster, default_priority: v})}
                  >
                    <SelectTrigger data-testid="edit-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={editingMaster.description || ''} 
                    onChange={(e) => setEditingMaster({...editingMaster, description: e.target.value})}
                    rows={3}
                    data-testid="edit-description"
                  />
                </div>
                <div>
                  <Label>Frequency (months)</Label>
                  <Input 
                    type="number" 
                    value={editingMaster.default_frequency_months || 12} 
                    onChange={(e) => setEditingMaster({...editingMaster, default_frequency_months: parseInt(e.target.value) || 12})}
                    data-testid="edit-frequency"
                  />
                </div>
                <div>
                  <Label>Default Trade</Label>
                  <Select 
                    value={editingMaster.default_trade || ''} 
                    onValueChange={(v) => setEditingMaster({...editingMaster, default_trade: v})}
                  >
                    <SelectTrigger data-testid="edit-trade">
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRADES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cost Min (£)</Label>
                  <Input 
                    type="number" 
                    value={editingMaster.cost_min || 0} 
                    onChange={(e) => setEditingMaster({...editingMaster, cost_min: parseFloat(e.target.value) || 0})}
                    data-testid="edit-cost-min"
                  />
                </div>
                <div>
                  <Label>Cost Max (£)</Label>
                  <Input 
                    type="number" 
                    value={editingMaster.cost_max || 0} 
                    onChange={(e) => setEditingMaster({...editingMaster, cost_max: parseFloat(e.target.value) || 0})}
                    data-testid="edit-cost-max"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editingMaster.is_legal_requirement || false}
                    onCheckedChange={(v) => setEditingMaster({...editingMaster, is_legal_requirement: v})}
                    data-testid="edit-legal"
                  />
                  <Label className="text-sm">Legal Requirement</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editingMaster.requires_certificate || false}
                    onCheckedChange={(v) => setEditingMaster({...editingMaster, requires_certificate: v})}
                    data-testid="edit-certificate"
                  />
                  <Label className="text-sm">Requires Certificate</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={editingMaster.active !== false}
                    onCheckedChange={(v) => setEditingMaster({...editingMaster, active: v})}
                    data-testid="edit-active"
                  />
                  <Label className="text-sm">Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveMaster} className="bg-[#1F7A78] hover:bg-[#16615f]" data-testid="save-master-btn">
              {editingMaster?.id ? 'Save Changes' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Assign PPM Task to Assets</DialogTitle>
            {assigningMaster && (
              <p className="text-sm text-slate-500">Assigning: {assigningMaster.task_name}</p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm">Selected: {selectedAssets.length} asset(s)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedAssets(assets.map(a => a.id))}>
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedAssets([])}>
                  Clear
                </Button>
              </div>
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-4 space-y-2">
                {assets.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No assets found. Add assets first before assigning PPM tasks.
                  </div>
                ) : (
                  assets.map(asset => (
                    <div 
                      key={asset.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedAssets.includes(asset.id) ? 'bg-[#1F7A78]/10 border-[#1F7A78]' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        setSelectedAssets(prev => 
                          prev.includes(asset.id) 
                            ? prev.filter(id => id !== asset.id)
                            : [...prev, asset.id]
                        );
                      }}
                      data-testid={`asset-${asset.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{asset.park_name}</div>
                          <div className="text-sm text-slate-500">
                            {asset.address} {asset.pitch_or_unit && `• Unit ${asset.pitch_or_unit}`}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {asset.asset_type} • {asset.heating_type} • {asset.occupancy_type}
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedAssets.includes(asset.id) ? 'bg-[#1F7A78] border-[#1F7A78]' : 'border-slate-300'
                        }`}>
                          {selectedAssets.includes(asset.id) && <Check className="h-3 w-3 text-white" />}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAssign} 
              className="bg-[#1F7A78] hover:bg-[#16615f]"
              disabled={selectedAssets.length === 0}
              data-testid="assign-confirm-btn"
            >
              Assign to {selectedAssets.length} Asset(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
